import type { FormFieldDefinitionModel } from './form-types';

/**
 * PD-16691 — a date or date-and-time question's default meaning "the day the visitor opens
 * the form" (an application's signing date, for example). Stored as this token in the
 * field's `defaultValue`, never as a date, and turned into a real value by the page that
 * draws the form, in the visitor's own time zone: the server's clock is UTC, so resolving
 * it there would hand a visitor in the evening tomorrow's date. The visitor can still
 * change it, and the submission stores the value the page sent, never the token.
 *
 * A page that draws forms itself (a website through its own helper) resolves the token the
 * same way: `formTodayValue(fieldType, new Date())` for a `date` or `datetime` question
 * whose `defaultValue` is `FORM_DEFAULT_TODAY`.
 */
export const FORM_DEFAULT_TODAY = '@today';

/** The question types that may default to today. */
export const FORM_DEFAULT_TODAY_TYPES: ReadonlyArray<string> = [
	'date',
	'datetime',
];

function twoDigits(value: number): string {
	return String(value).padStart(2, '0');
}

/**
 * Today in the local time zone of whoever runs this (the visitor's browser): `YYYY-MM-DD`
 * for a date question, `YYYY-MM-DDTHH:mm` for a date-and-time one, which is what a date
 * picker and a browser `datetime-local` input hold and what the shared validator accepts.
 * Built from the local getters, never from toISOString(), which is UTC.
 */
export function formTodayValue(
	fieldType: 'date' | 'datetime',
	now: Date,
): string {
	const day = `${now.getFullYear()}-${twoDigits(now.getMonth() + 1)}-${twoDigits(now.getDate())}`;
	return fieldType === 'date'
		? day
		: `${day}T${twoDigits(now.getHours())}:${twoDigits(now.getMinutes())}`;
}

/** Whether a field's default is the "today" token (and the field can use it). */
export function defaultsToToday(
	field: Pick<FormFieldDefinitionModel, 'fieldType' | 'defaultValue'>,
): boolean {
	return (
		field.defaultValue === FORM_DEFAULT_TODAY &&
		FORM_DEFAULT_TODAY_TYPES.includes(field.fieldType)
	);
}
